import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  AnnotationActionMenuPosition,
  AnnotationImageData,
  Coords,
  pageImageData,
  Point,
  PreviewAction,
  PreviewActionType,
} from './Preview.types';
import useSessionUser from '../../hooks/useSessionUser';
import { getImageUnderPointer, getTargetPageImage, getTargetPageImageToInsertAnnotation } from './previewUtils';
import {
  addAnnotationOnOrgz,
  deleteAnnotationOnOrgz,
  updateAnnotationOnOrgz,
} from '../../services/exams/examTaskGroup/examTaskGroup.service';
import { AxiosResponse } from 'axios';
import { useLayoutContext } from '../../Context/Layout.context';
import { fabric } from 'fabric';
import { MouseButtons } from '../../const/mouse';
import {
  AnnotationDefinition,
  AnnotationOnImage,
  AnnotationType,
  AnnotationDefinitionCore,
  convertAnnotationDefinitionToAnnotationDefinitionCore,
} from '../../models/annotation.types';
import { ExamScoringSchema, ExamTaskGroupSolutionScoreShelfResponse } from '../../models/assessment.types';
import { ActionMenuItem } from '../ActionsMenu/ActionsMenu.types';
import ActionsMenuItems from '../ActionsMenu/ActionsMenuItems.component';
import { Prompt } from '../Prompt/Prompt.component';
import { useScoring } from '../../Context/Scoring.context';
import { getCriterionFromTasks } from '../../Context/helpers';
import { Status } from '../../services/exams/assessmentSession/assessments.types';
import useAnnotationsKeyboard from '../../hooks/useAnnotationsKeyboard';
import {
  getAnnotationDefinitionCore,
  setupDynamicAnnotation,
  createNewAnnotationPositionBasedOnImage,
  beforeRemove,
  getAnnotationIdFromImg,
  removeAnnotationImagesFromCanvas,
  rotateAnnotation,
  getAnnotationWithUpdatedLayers,
} from './annotationsOnPageManagerService';
import { useAnnotations } from './useAnnotations';
import { useAnnotationsImageSetup } from './useAnnotationsImageSetup';
import { ANNOTATION_OBJECT_NAME, MINIMUM_ANNOTATION_SIZE } from './annotationsConsts';
import { useAnnotationCursor } from './useAnnotationCursor';
import { useDynamicCreaton } from './useDynamicCreaton';

const ANNOTATION_UPDATE_KEYBOARD_DELAY = 550;

interface AnnotationsOnPageManagerProps {
  canAddAnnotation: boolean;
  topCanvasMargin: number;
  actionsToTrigger: PreviewAction[];
  onActionTrigger: (action: PreviewAction) => void;
  onAddAnnotation: (annotation: AnnotationOnImage) => void;
  onBeforeAddAnnotation: () => void;
  onAnnotationChanged: (annotations: AnnotationOnImage[]) => void;
  onAnnotationDeleted: (annotation: AnnotationOnImage) => void;
  onAnnotationDeactivate: () => void;
  getPopoverCoord: (left: number, top: number, zoom: number, offset: Coords) => Point;
  pageAreAddedToCanvas: boolean;
  examSheetPagesLoaded: boolean;
  imagesCount: number;
  imageZonesLoaded: boolean;
  scoreState?: Status | null;
  setIsAnnotationSelected?: (isSelected: boolean) => void;
  selectPreviousActiveAnnotation?: () => void;
  canvasContainer: RefObject<HTMLDivElement | null>;
  updateIsMouseScrollDisabled: (value: boolean) => void;
}

const AnnotationsOnPageManager: React.FC<AnnotationsOnPageManagerProps> = (props) => {
  const { onBeforeAddAnnotation, updateIsMouseScrollDisabled } = props;
  const mouseKeyDownInProgress = useRef<boolean>(false);
  const mouseKeyAlreadyUpEvent = useRef<fabric.IEvent<MouseEvent> | null>(null);
  const { isUserKK, isUserKGZ } = useSessionUser();
  const { openAlert, showLoader } = useLayoutContext();
  const { getNextScore, getPrevScore, updateScore, openScoreShelf, isCommentForCurrentScoreProvided } = useScoring();
  const {
    activeAnnotation,
    setActiveAnnotation,
    annotationVersions,
    activeAnnotationId,
    activeCriterion,
    activeTask,
    addingAnnotationEnabled,
    annotationDefinitions,
    annotationImages,
    annotations,
    annotationsVisible,
    canEditRemoveAnnotation,
    canUserEditRemoveAnnotation,
    canvas,
    canvasElement,
    cursorDimensions,
    getCanvasPointsForAnnotation,
    isActiveAnnotationForGrading,
    pageImages,
    rotateAngle,
    setAnnotationImages,
    setLastAddedAnnotationId,
    lastAddedAnnotationId,
    currentCoords,
    unlockAllAnnotations,
    activeAnnotationsRef,
    lockAllAnnotations,
  } = useAnnotations();

  const [annotationActionMenuPosition, setAnnotationActionMenuPosition] = useState<
    AnnotationActionMenuPosition | undefined
  >(undefined);
  const [isMaxScorePromptVisible, setIsMaxScorePromptVisible] = useState<boolean>(false);
  const annotationsRendering = useRef<boolean>(false);
  const [updateScoreModalHandler, setUpdateScoreModalHandler] = useState<(() => void) | null>(null);
  const [updateScoreModalOn, setUpdateScoreModalOn] = useState<boolean>(false);

  if (!canvas) return null;

  const { setCurrentCursor } = useAnnotationCursor({
    canAddAnnotation: props.canAddAnnotation,
  });

  const updateAnnotation = (updatedAnnotations: AnnotationOnImage[], changeLayers: boolean, isDynamic?: boolean) => {
    updateAnnotationOnOrgz(updatedAnnotations).then(
      (response: AxiosResponse<boolean>) => {
        if (response.data) {
          if (!changeLayers) setLastAddedAnnotationId(updatedAnnotations[0].annotationId);
          setAnnotationActionMenuPosition(undefined);
          props.onAnnotationChanged(updatedAnnotations);
          return;
        }
        openAlert('save_error');
      },
      () => {
        openAlert('save_error');
      },
    );
  };

  const updateAnnotationPositionOnOrgz = (annotationOnImage: AnnotationOnImage | null, image: fabric.Object) => {
    let annotationToUpdate: AnnotationOnImage | null = annotationOnImage;

    if (annotationToUpdate === null && image.left && image.top && activeAnnotationsRef.current) {
      const annotationId = image.name?.substring(ANNOTATION_OBJECT_NAME.length) ?? '';
      const activeAnnotation = activeAnnotationsRef.current.find((x) => x.annotation.annotationId === annotationId);
      if (!activeAnnotation) return;

      annotationToUpdate = createNewAnnotationPositionBasedOnImage(pageImages, image, activeAnnotation.annotation);
      if (!annotationToUpdate) return;
    }

    if (!annotationToUpdate) return;

    const annotationDefinitionCore = getAnnotationDefinitionCore(
      annotationToUpdate,
      activeTask,
      annotationVersions,
      annotationDefinitions,
    );

    if (annotationDefinitionCore?.annotationType === AnnotationType.dynamic) {
      setupDynamicAnnotation(annotationToUpdate, annotationDefinitionCore, cursorDimensions.current, image);
    }

    const isDynamic = annotationDefinitionCore?.annotationType === AnnotationType.dynamic;

    updateAnnotation([annotationToUpdate], false, isDynamic);
  };

  const removeAnnotation = (annotation: AnnotationOnImage) => {
    showLoader(true, 'Usuwanie adnotacji');
    deleteAnnotationOnOrgz(annotation.annotationId)
      .then(
        () => {
          props.onAnnotationDeleted(annotation);
          const annotationImageName = ANNOTATION_OBJECT_NAME + annotation.annotationId;
          const toDelete = annotationImages.find((item) => item.image.name === annotationImageName);
          if (toDelete) canvas.remove(toDelete.image);
          setAnnotationActionMenuPosition(undefined);
        },
        () => openAlert('delete_error'),
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleRemoveAnnotation = (annotationId?: string) => {
    let annotation: AnnotationOnImage | null = null;
    if (!annotationActionMenuPosition && !annotationId) return;

    if (annotationId) {
      const foundAnnotation = activeAnnotationsRef.current.find((x) => x.annotation.annotationId === annotationId);
      if (!foundAnnotation) return;
      annotation = foundAnnotation.annotation;
    } else if (annotationActionMenuPosition && !annotationId) {
      annotation = annotationActionMenuPosition.annotation;
    } else {
      return;
    }
    if (activeCriterion && activeCriterion.isScoringAnnotationRequired && annotation.forScoring) {
      const prevScore = getPrevScore();

      updateScore(
        prevScore,
        async (
          scoreCandidate: number | null,
          criterion: ExamScoringSchema,
          responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined,
        ) => {
          const annotationToDelete = beforeRemove(annotation!, responseData, activeCriterion);
          if (annotationToDelete) removeAnnotation(annotationToDelete);
        },
      );
      return;
    }
    if (
      props.scoreState &&
      (props.scoreState === Status.Assessed || (props.scoreState === Status.Closed && (isUserKGZ || isUserKK)))
    ) {
      openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
        const annotationToDelete = beforeRemove(annotation!, responseData, activeCriterion);
        if (annotationToDelete) removeAnnotation(annotationToDelete);
      });
      return;
    }
    removeAnnotation(annotation);
  };

  const onPageDownOrUp = useCallback(
    (img: fabric.Object | null, direction: 'up' | 'down') => {
      if (!canvas) return;

      const annotations = [...activeAnnotationsRef.current.map((x) => x.annotation)];
      let annotationId: string | null = null;
      if (img) annotationId = getAnnotationIdFromImg(img);
      else if (annotationActionMenuPosition) annotationId = annotationActionMenuPosition.annotation.annotationId;

      if (!annotationId) return;

      const updatedAnnotations = getAnnotationWithUpdatedLayers(annotationId, annotations, direction);

      canvas.discardActiveObject();
      updateAnnotation(updatedAnnotations, true, false);
    },
    [canvas, annotationActionMenuPosition],
  );

  const onDelete = useCallback(
    (annotationId: string) => {
      handleRemoveAnnotation(annotationId);
    },
    [handleRemoveAnnotation],
  );

  useAnnotationsKeyboard({
    canvas: canvas,
    canvasContainerRef: props.canvasContainer,
    updateDelay: ANNOTATION_UPDATE_KEYBOARD_DELAY,
    lastAddedId: lastAddedAnnotationId,
    setIsAnnotationSelected: props.setIsAnnotationSelected,
    onAnnotationPositionUpdate: updateAnnotationPositionOnOrgz,
    onEscKey: props.selectPreviousActiveAnnotation,
    onDeleteKey: onDelete,
    onPageDownOrUpKey: onPageDownOrUp,
  });

  const handleAnnotationRightClick = (event: fabric.IEvent, annotation: AnnotationOnImage, image: fabric.Object) => {
    if (canEditRemoveAnnotation && canUserEditRemoveAnnotation(annotation) && image) {
      if (!canvasElement.current) {
        throw new Error('empty canvas!');
      }
      const zoom = canvas.getZoom();
      const { hasControls, hasBorders, left, top } = image;
      image.set({
        hasControls: false,
        hasBorders: true,
      });
      canvas.setActiveObject(image);
      canvas.renderAll();
      const canvasOffset = fabric.util.getElementOffset(canvasElement.current);
      const menuCoords = event.absolutePointer
        ? props.getPopoverCoord(event.absolutePointer.x, event.absolutePointer.y, zoom, canvasOffset)
        : props.getPopoverCoord(left || 0, top || 0, zoom, canvasOffset);
      menuCoords &&
        setAnnotationActionMenuPosition({
          annotation,
          position: { left: menuCoords.x, top: menuCoords.y - props.topCanvasMargin },
          imageSettings: { hasControls: hasControls!, hasBorders: !hasBorders },
        });
    }
  };

  const { loadAndDrawAnnotationsAsync, createNewAnnotation, removeTempAnnotationFromCanvas } = useAnnotationsImageSetup(
    {
      onAnnotationRightClick: handleAnnotationRightClick,
      onStoreAnnotation: (updatedAnnotation, isDynamic) => updateAnnotation([updatedAnnotation], false, isDynamic),
      isAnnotationActive: !!activeAnnotation,
    },
  );

  const {
    handleMouseDown: handleDynamicCreationMouseDown,
    handleMouseMove: handleDynamicCreationMouseMove,
    handleMouseUp: handleDynamicCreationMouseUp,
  } = useDynamicCreaton({
    onAnnotationRightClick: handleAnnotationRightClick,
    onStoreAnnotation: (updatedAnnotation, isDynamic) => updateAnnotation([updatedAnnotation], false, isDynamic),
    isAnnotationActive: !!activeAnnotation,
  });

  const clearAnnotationImages = (): void => {
    removeAnnotationImagesFromCanvas(activeAnnotationsRef, annotationImages, canvas);
    setAnnotationImages([]);
  };

  const addAnnotation = (
    x: number,
    y: number,
    targetImageData: pageImageData,
    annotationDefinitionCore: AnnotationDefinitionCore,
    criterion: ExamScoringSchema,
    width?: number,
    height?: number,
    angle?: number,
  ): Promise<AnnotationOnImage> => {
    return new Promise((resolve, reject) => {
      lockAllAnnotations();
      const newAnnotation = createNewAnnotation(
        x,
        y,
        targetImageData,
        annotationDefinitionCore,
        criterion,
        width,
        height,
        angle,
      );
      if (!newAnnotation) {
        if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
          removeTempAnnotationFromCanvas();
        }
        return;
      }

      addAnnotationOnOrgz(criterion.partialScoreId, newAnnotation).then(
        (response: AxiosResponse<string>) => {
          newAnnotation.annotationId = response.data;
          setLastAddedAnnotationId(response.data);
          props.onAddAnnotation(newAnnotation);
          resolve(newAnnotation);
        },
        () => {
          openAlert('save_error');
          if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
            removeTempAnnotationFromCanvas();
          }
          reject();
        },
      );
    });
  };

  const insertAnnotationOnCanvas = (
    x: number,
    y: number,
    targetObject?: fabric.Object,
    width?: number,
    height?: number,
    angle?: number,
    page?: pageImageData | null | undefined,
  ) => {
    let newAnnotation = null;
    if (
      activeAnnotation &&
      activeCriterion &&
      cursorDimensions.current &&
      activeTask &&
      !activeTask.noAnswer &&
      addingAnnotationEnabled &&
      props.canAddAnnotation &&
      rotateAngle === 0
    ) {
      const nextScore = getNextScore();
      if (nextScore === null && activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) {
        setIsMaxScorePromptVisible(true);
        removeTempAnnotationFromCanvas();
      } else {
        const targetImageData = getTargetPageImageToInsertAnnotation(page ? [page] : pageImages, targetObject);
        if (targetImageData) {
          const annotationDefinitionCore = convertAnnotationDefinitionToAnnotationDefinitionCore(activeAnnotation);
          if (activeCriterion.isScoringAnnotationRequired && isActiveAnnotationForGrading) {
            setUpdateScoreModalHandler(() => () => {
              updateScore(nextScore, async (_, criterion: ExamScoringSchema) => {
                if (activeCriterion) {
                  return await addAnnotation(
                    x,
                    y,
                    targetImageData,
                    annotationDefinitionCore,
                    criterion,
                    width,
                    height,
                    angle,
                  );
                } else {
                  removeTempAnnotationFromCanvas();
                  return false;
                }
              });
              setUpdateScoreModalOn(false);
            });
            if (isCommentForCurrentScoreProvided) {
              setUpdateScoreModalOn(true);
            } else {
              updateScore(nextScore, async (newScore: number | null, criterion: ExamScoringSchema) => {
                if (activeCriterion) {
                  return await addAnnotation(
                    x,
                    y,
                    targetImageData,
                    annotationDefinitionCore,
                    criterion,
                    width,
                    height,
                    angle,
                  );
                } else {
                  removeTempAnnotationFromCanvas();
                  return false;
                }
              });
            }
          } else {
            if (props.scoreState && (props.scoreState === Status.Assessed || isUserKGZ || isUserKK)) {
              openScoreShelf(async (responseData: ExamTaskGroupSolutionScoreShelfResponse | undefined) => {
                let shelfActiveCriterion = null;
                if (responseData && activeCriterion) {
                  const newActiveCriterion = getCriterionFromTasks(
                    responseData.examTasksCriteriaDto.examTasksCriteria,
                    activeCriterion.examScoringSchemaId,
                  );
                  if (newActiveCriterion) {
                    shelfActiveCriterion = newActiveCriterion;
                  }
                }
                newAnnotation = await addAnnotation(
                  x,
                  y,
                  targetImageData,
                  annotationDefinitionCore,
                  shelfActiveCriterion ?? activeCriterion,
                  width,
                  height,
                  angle,
                );
              });
            } else {
              newAnnotation = addAnnotation(
                x,
                y,
                targetImageData,
                annotationDefinitionCore,
                activeCriterion,
                width,
                height,
                angle,
              );
            }
          }
        }
      }
    }
    return newAnnotation;
  };

  const handleCanvasMouseMove = useCallback(
    (event: fabric.IEvent<MouseEvent>) => {
      if (!activeAnnotation || !activeCriterion || !activeTask || mouseKeyDownInProgress.current) {
        return;
      }

      if (activeAnnotation?.annotationType === AnnotationType.dynamic) {
        handleDynamicCreationMouseMove(event);
      }

      if (cursorDimensions.current && !activeTask?.noAnswer) {
        const canvasPoints = getCanvasPointsForAnnotation(event.e.x, event.e.y);
        const targetImageData: pageImageData | undefined = getTargetPageImage(
          pageImages,
          canvasPoints.x,
          canvasPoints.y,
        );
        setCurrentCursor(!targetImageData);
      }
    },
    [
      activeAnnotation,
      activeCriterion,
      activeTask,
      cursorDimensions,
      getCanvasPointsForAnnotation,
      handleDynamicCreationMouseMove,
      pageImages,
      setCurrentCursor,
    ],
  );

  const handleCanvasMouseUp = useCallback(
    (event: fabric.IEvent<MouseEvent>) => {
      mouseKeyAlreadyUpEvent.current = event;
      if (mouseKeyDownInProgress.current) {
        return;
      }
      if (event.button === MouseButtons.left) {
        let passPageToInsert = false;
        const pointer = canvas.getPointer(event.e);
        const page = getImageUnderPointer(pageImages, pointer);
        if (!page) {
          return;
        }
        if (
          event.target?.name &&
          event.target.name.substring(0, ANNOTATION_OBJECT_NAME.length) === ANNOTATION_OBJECT_NAME
        ) {
          passPageToInsert = true;
        }

        if (activeAnnotation?.annotationType === AnnotationType.dynamic) {
          const annotationCoordinates = handleDynamicCreationMouseUp(event);
          if (annotationCoordinates) {
            const { x, y, width, height, angle } = annotationCoordinates;
            insertAnnotationOnCanvas(
              x,
              y,
              event.target,
              Math.max(width, MINIMUM_ANNOTATION_SIZE),
              Math.max(height, MINIMUM_ANNOTATION_SIZE),
              angle,
              passPageToInsert ? page : null,
            );
            updateIsMouseScrollDisabled(false);
            return;
          }
        }
        insertAnnotationOnCanvas(event.e.x, event.e.y, event.target, undefined, undefined, undefined, page);
        updateIsMouseScrollDisabled(false);
      }
    },
    [
      activeAnnotation?.annotationType,
      canvas,
      handleDynamicCreationMouseUp,
      insertAnnotationOnCanvas,
      pageImages,
      updateIsMouseScrollDisabled,
    ],
  );

  const handleCanvasMouseDown = useCallback(
    async (event: fabric.IEvent<MouseEvent>) => {
      mouseKeyAlreadyUpEvent.current = null;
      mouseKeyDownInProgress.current = true;
      try {
        if (!activeAnnotation) {
          return;
        } else {
          lockAllAnnotations();
          setLastAddedAnnotationId(null);
        }
        onBeforeAddAnnotation();
        if (
          activeAnnotation.annotationType === AnnotationType.dynamic &&
          (await handleDynamicCreationMouseDown(event))
        ) {
          updateIsMouseScrollDisabled(true);
        }
      } finally {
        mouseKeyDownInProgress.current = false;
      }
      if (mouseKeyAlreadyUpEvent.current) {
        handleCanvasMouseMove(mouseKeyAlreadyUpEvent.current);
        handleCanvasMouseUp(mouseKeyAlreadyUpEvent.current);
      }
    },
    [
      activeAnnotation,
      onBeforeAddAnnotation,
      handleDynamicCreationMouseDown,
      lockAllAnnotations,
      setLastAddedAnnotationId,
      updateIsMouseScrollDisabled,
      handleCanvasMouseMove,
      handleCanvasMouseUp,
    ],
  );

  const annotationActionMenuItems: ActionMenuItem[] = [
    {
      text: 'Przesuń na wierzch',
      onClick: () => onPageDownOrUp(null, 'up'),
    },
    {
      text: 'Przesuń na spód',
      onClick: () => onPageDownOrUp(null, 'down'),
    },
    {
      text: 'Usuń adnotację',
      onClick: () => handleRemoveAnnotation(),
    },
  ];

  useEffect(() => {
    if (!props.pageAreAddedToCanvas || !props.examSheetPagesLoaded || !pageImages.length) return;

    if (props.pageAreAddedToCanvas && annotationsVisible) {
      if (annotationsRendering.current) return;
      annotationsRendering.current = true;
      loadAndDrawAnnotationsAsync(activeAnnotationsRef, annotationsRendering);
    } else {
      if (annotationsRendering.current) return;
      clearAnnotationImages();
    }
  }, [
    props.pageAreAddedToCanvas,
    activeTask?.taskId,
    activeCriterion?.examScoringSchemaId,
    props.examSheetPagesLoaded,
    annotationsVisible,
    annotations,
  ]);

  const rotateAnnotationImages = (pageImageCoords: Coords) => {
    annotationImages.forEach((annotationImage: AnnotationImageData) => {
      rotateAnnotation(annotationImage, pageImageCoords, rotateAngle, currentCoords);
    });
  };

  useEffect(() => {
    props.actionsToTrigger.forEach((action) => {
      switch (action.type) {
        case PreviewActionType.clearAnnotations: {
          clearAnnotationImages();
          props.onActionTrigger(action);
          break;
        }
        case PreviewActionType.rotateAnnotations: {
          if (annotationImages.length) {
            rotateAnnotationImages(action.payload as Coords);
            props.onActionTrigger(action);
          }
          break;
        }
      }
    });
  }, [props.actionsToTrigger]);

  useEffect(() => {
    setActiveAnnotation(null);
    props.onAnnotationDeactivate();
  }, [activeCriterion?.examScoringSchemaId]);

  useEffect(() => {
    if (activeAnnotation) {
      lockAllAnnotations();
      canvas.off('mouse:down');
      canvas.on('mouse:down', handleCanvasMouseDown);
      canvas.off('mouse:up');
      canvas.on('mouse:up', handleCanvasMouseUp);
      canvas.off('mouse:move');
      canvas.on('mouse:move', handleCanvasMouseMove);
    } else {
      canvas.off('mouse:down');
      canvas.off('mouse:up');
      canvas.off('mouse:move');
    }
  }, [
    activeAnnotation,
    activeAnnotationId,
    activeCriterion,
    addingAnnotationEnabled,
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    handleCanvasMouseUp,
    pageImages,
    props.canAddAnnotation,
    rotateAngle,
  ]);

  useEffect(() => {
    if (!activeAnnotationId) {
      setActiveAnnotation(null);
    } else {
      const annotationDefinition = annotationDefinitions.find(
        (item: AnnotationDefinition) => item.id === activeAnnotationId,
      );
      if (annotationDefinition) {
        setActiveAnnotation(annotationDefinition);
      }
    }
  }, [activeAnnotationId, annotationDefinitions]);

  const closeActionsMenu = () => {
    const image = canvas.getActiveObject();
    if (image && annotationActionMenuPosition?.imageSettings) {
      image.set({
        ...annotationActionMenuPosition.imageSettings,
      });
    }

    canvas.discardActiveObject();
    canvas.renderAll();
    setAnnotationActionMenuPosition(undefined);
  };

  useEffect(() => {
    if (activeAnnotation) {
      lockAllAnnotations();
    } else if (activeAnnotation && lastAddedAnnotationId) {
      unlockAllAnnotations();
      const lastAddedAnnotation = annotationImages.find(
        (item) => item.annotation.annotationId === lastAddedAnnotationId,
      );
      if (lastAddedAnnotation?.image) {
        lastAddedAnnotation.image.set({
          hasBorders: true,
        });
        canvas.setActiveObject(lastAddedAnnotation.image).renderAll();
      }
    }
  }, [activeAnnotation]);

  return (
    <>
      {annotationActionMenuPosition && (
        <ActionsMenuItems
          menuItems={annotationActionMenuItems}
          position={annotationActionMenuPosition.position}
          onCloseMenu={closeActionsMenu}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        />
      )}

      <Prompt
        type="confirmation"
        size="sm"
        open={updateScoreModalOn}
        onConfirm={() => updateScoreModalHandler?.()}
        onCancel={() => setUpdateScoreModalOn(false)}
        mainActionButtonText="TAK"
        cancelButtonText="NIE"
        isBold={true}
        mainText={
          'Zmieniłeś punktację dla kryterium. Aktualna ocena nie wymaga komentarza. Czy chcesz usunąć istniejący komentarz i zapisać zmiany?'
        }
      />

      {isMaxScorePromptVisible && (
        <Prompt
          type="error"
          open={true}
          mainText={
            'Nie można dodać kolejnej adnotacji ponieważ w wybranym kryterium została już przydzielona maksymalna liczba punktów.'
          }
          isBold={true}
          onConfirm={() => {
            setIsMaxScorePromptVisible(false);
          }}
        />
      )}
    </>
  );
};

export default AnnotationsOnPageManager;
